import { Separator } from "@/components/ui/separator";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system/ui";
import { AppBreadcrumb } from "./common/app-breadcrumb";
import { NotificationsPopover } from "./common/notifications-popover";
import { useHeaderButton } from "@/contexts/header-button-context";
import { NavLink } from "react-router";
import { useAuth } from "@/contexts/auth-provider/useAuth";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { useTheme } from "@/hooks/use-theme";
import { useReverb } from "@/hooks/use-reverb";
import { useMemo, useState } from "react";
import * as Sentry from "@sentry/react";

interface SiteHeaderProps {
  showBreadcrumbs?: boolean;
}
export function SiteHeader({
  showBreadcrumbs = true,
}: Readonly<SiteHeaderProps>) {
  const { isMobile } = useSidebar();
  const { Comp } = useHeaderButton();

  // Initialize WebSocket connection for real-time notifications
  useReverb();

  return (
    <header
      className="w-full bg-neutral-foreground rounded-t-lg sticky top-0 z-10 flex h-(--header-height) shrink-0 items-center gap-2 border-b border-neutral-silent transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)"
      data-testid="site-header"
    >
      {/* mx-auto max-w-[var(--size-horizontal-3xl)] */}
      <div className="flex w-full items-center justify-between gap-1 pl-6 pr-4 lg:gap-2 lg:px-8 ">
        <div className="flex items-center gap-1 lg:gap-2">
          {isMobile && (
            <>
              <SidebarTrigger className="-ml-1" />
              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4"
              />
            </>
          )}
          {showBreadcrumbs && <AppBreadcrumb />}
        </div>

        <div className="flex items-center gap-2">
          {Comp ?? <NotificationsPopover />}
        </div>
      </div>
    </header>
  );
}

export const UserMenu = () => {
  const { keycloak, authUser } = useAuth();
  const { hasPermission } = useAuthorization();
  const { theme, toggleTheme } = useTheme();
  const { isMobile } = useSidebar();
  const canAccessPrivacyPolicies = hasPermission(
    Permissions["privacy-policies"].view,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const iconTheme = useMemo(() => {
    if (theme === "system") return "display";
    if (theme === "dark") return "moon-stars";
    return "sun-bright";
  }, [theme]);

  const themeLabel = useMemo(() => {
    if (theme === "system") return "Sistema";
    if (theme === "dark") return "Oscuro";
    return "Claro";
  }, [theme]);

  return (
    <Tooltip open={isTooltipOpen && !isOpen} onOpenChange={setIsTooltipOpen}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          {authUser ? (
            <TooltipTrigger
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsTooltipOpen(false);
                setIsOpen(true);
              }}
            >
              <div
                className="flex gap-3 cursor-pointer group-data-[collapsible=icon]:justify-center"
                data-testid="user-menu-trigger"
              >
                <Avatar size="lg" shape="square" color="action">
                  <AvatarFallback shape="square">
                    {authUser.name ? `${authUser.name.charAt(0)}` : ""}
                  </AvatarFallback>
                </Avatar>
                <div className="group-data-[collapsible=icon]:hidden overflow-hidden">
                  <p className="w-full text-start text-body-loud font-semibold text-size-body-sm line-clamp-1 truncate">
                    {authUser.name ?? `${authUser.name}`}
                  </p>
                  <p className="w-full text-start text-body-quiet text-size-body-xs line-clamp-1 truncate">
                    {authUser.email}
                  </p>
                </div>
              </div>
            </TooltipTrigger>
          ) : (
            <Button
              variant="quiet"
              size="sm"
              className="size-9"
              data-testid="fallback-user-menu-trigger"
            >
              <Icon
                type="far"
                name="user-circle"
                className="text-current text-sm leading-2xl"
              />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg border-neutral-silent text-body-loud font-semibold p-2"
          side={isMobile ? "top" : "right"}
          align={isMobile ? "start" : "end"}
          sideOffset={18}
          alignOffset={-7}
        >
          <DropdownMenuGroup>
            <DropdownMenuItem
              asChild
              className="hover:bg-neutral-object-accent-silent font-normal text-body cursor-pointer"
            >
              <NavLink
                to="/perfil"
                aria-label="policies"
                className="flex items-center gap-2 w-full p-2"
              >
                <Icon
                  type="far"
                  name="user-circle"
                  className="text-current text-base leading-3xl"
                />
                Perfil
              </NavLink>
            </DropdownMenuItem>

            {canAccessPrivacyPolicies && (
              <DropdownMenuItem
                asChild
                className="hover:bg-neutral-object-accent-silent font-normal text-body p-2 cursor-pointer"
              >
                <NavLink
                  to="/legal-info"
                  aria-label="policies"
                  className="flex items-center gap-2 w-full"
                >
                  <Icon
                    type="far"
                    name="shield-keyhole"
                    className="text-current text-base leading-3xl"
                  />
                  <span>Políticas de privacidad</span>
                </NavLink>
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent font-normal text-body p-2 cursor-pointer"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              toggleTheme();
            }}
            data-testid="theme-toggle-button"
          >
            <Icon
              type="far"
              name={iconTheme}
              className="text-current text-base leading-3xl"
            />
            <span className="font-semibold">Tema</span>{" "}
            <span className="text-body-quiet text-md ">{themeLabel}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="border-t border-neutral mx-2 my-2.5" />
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent font-normal text-body p-2 cursor-pointer"
            onClick={() => {
              if (keycloak) {
                Sentry.setUser(null);
                keycloak.logout({
                  redirectUri: globalThis.location.origin,
                });
              } else {
                console.warn("Keycloak instance not available for logout.");
              }
            }}
          >
            <Icon
              type="far"
              name="power-off"
              className="text-current text-base leading-3xl"
            />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {authUser ? (
        <TooltipContent side="top" align="start">
          <div className="group-data-[collapsible=icon]:hidden overflow-hidden">
            <p className="w-full text-body-loud font-semibold text-size-body-sm line-clamp-1 truncate">
              {authUser.name ?? `${authUser.name}`}
            </p>
            <p className="w-full text-start text-body-quiet text-size-body-xs line-clamp-1 truncate">
              {authUser.email}
            </p>
          </div>
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
};
